
import { useEffect, useRef, useState } from 'react';

interface Project {
  id: number;
  image: string;
  title: string;
  category: string;
}

export default function SignatureWork() {
  const [visibleProjects, setVisibleProjects] = useState<number[]>([]);
  const [hoveredProject, setHoveredProject] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const projects: Project[] = [
    {
      id: 1,
      title: 'The Meridian Residence',
      image: 'https://readdy.ai/api/search-image?query=luxury%20residential%20interior%20living%20room%20with%20floor%20to%20ceiling%20windows%2C%20custom%20millwork%20wall%20panels%20in%20natural%20oak%2C%20polished%20marble%20flooring%2C%20sophisticated%20neutral%20palette%2C%20architectural%20photography%2C%20natural%20daylight%2C%20high%20end%20finishing%20details%2C%20editorial%20style%20composition&width=1200&height=900&seq=work001&orientation=landscape',
      category: 'Residential / Banana Island'
    },
    {
      id: 2,
      title: 'Azure Hotel Lobby',
      image: 'https://readdy.ai/api/search-image?query=elegant%20boutique%20hotel%20lobby%20interior%20with%20custom%20brass%20fixtures%2C%20textured%20stone%20accent%20wall%2C%20refined%20furniture%20arrangement%2C%20warm%20ambient%20lighting%2C%20luxury%20hospitality%20design%2C%20architectural%20detail%20photography%2C%20sophisticated%20material%20palette&width=800&height=600&seq=work002&orientation=landscape',
      category: 'Hospitality / Victoria Island'
    },
    {
      id: 3,
      title: 'Sterling Executive Suite',
      image: 'https://readdy.ai/api/search-image?query=modern%20executive%20office%20interior%20with%20bespoke%20walnut%20wood%20paneling%2C%20integrated%20lighting%20design%2C%20minimalist%20furniture%2C%20floor%20to%20ceiling%20glass%2C%20premium%20finishing%20materials%2C%20architectural%20photography%2C%20clean%20lines%20and%20sophisticated%20details&width=800&height=600&seq=work003&orientation=landscape',
      category: 'Commercial / Ikoyi'
    },
    {
      id: 4,
      title: 'Penthouse Retreat',
      image: 'https://readdy.ai/api/search-image?query=luxury%20penthouse%20master%20bedroom%20with%20custom%20headboard%20wall%20feature%2C%20premium%20textile%20finishes%2C%20subtle%20accent%20lighting%2C%20refined%20color%20palette%2C%20high%20end%20residential%20interior%2C%20architectural%20photography%2C%20sophisticated%20minimalist%20design&width=800&height=600&seq=work004&orientation=landscape',
      category: 'Residential / Lekki Phase 1'
    },
    {
      id: 5,
      title: 'The Members Club',
      image: 'https://readdy.ai/api/search-image?query=private%20club%20lounge%20interior%20with%20rich%20leather%20upholstery%2C%20custom%20millwork%20bar%20area%2C%20ambient%20mood%20lighting%2C%20premium%20material%20finishes%2C%20sophisticated%20hospitality%20design%2C%20architectural%20detail%20photography%2C%20warm%20elegant%20atmosphere&width=800&height=600&seq=work005&orientation=landscape',
      category: 'Institutional / Lagos'
    },
    {
      id: 6,
      title: 'Contemporary Gallery',
      image: 'https://readdy.ai/api/search-image?query=contemporary%20art%20gallery%20interior%20space%20with%20polished%20concrete%20floors%2C%20track%20lighting%20system%2C%20white%20walls%20with%20subtle%20texture%2C%20minimalist%20architectural%20design%2C%20museum%20quality%20finishing%2C%20editorial%20photography%2C%20clean%20sophisticated%20aesthetic&width=1200&height=900&seq=work006&orientation=landscape',
      category: 'Cultural / Victoria Island'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = parseInt(entry.target.getAttribute('data-project-id') || '0');
            setVisibleProjects((prev) => [...new Set([...prev, id])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const projectElements = sectionRef.current?.querySelectorAll('[data-project-id]');
    projectElements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full bg-[#0A0A0A] py-16 md:py-24 lg:py-32 px-4 sm:px-6 md:px-8 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 md:mb-16 lg:mb-20 gap-6">
          <div>
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="w-8 md:w-12 h-px bg-[#C9A86C]" />
              <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase">Portfolio</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-serif text-white">
              Selected
              <span className="block text-[#C9A86C]">Spaces</span>
            </h2>
          </div>
          <p className="text-white/50 max-w-sm text-left md:text-right text-sm md:text-base">
            We interpret spaces. We do not replicate styles. Each project is a unique expression of precision and vision.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* Large Project 1 */}
          <div
            data-project-id={projects[0].id}
            onMouseEnter={() => setHoveredProject(projects[0].id)}
            onMouseLeave={() => setHoveredProject(null)}
            className={`col-span-1 md:col-span-12 lg:col-span-8 relative h-[50vh] md:h-[60vh] lg:h-[70vh] overflow-hidden group cursor-pointer transition-all duration-700 ${
              visibleProjects.includes(projects[0].id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <img
              src={projects[0].image}
              alt={projects[0].title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-80" />
            <div className={`absolute inset-0 bg-[#C9A86C]/20 transition-opacity duration-500 ${hoveredProject === projects[0].id ? 'opacity-100' : 'opacity-0'}`} />
            
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 lg:p-8">
              <span className="text-[#C9A86C] text-xs md:text-sm tracking-widest uppercase mb-2 md:mb-3 block">{projects[0].category}</span>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-serif text-white mb-2 md:mb-4">{projects[0].title}</h3>
              <div className={`flex items-center gap-2 text-white transition-all duration-500 ${hoveredProject === projects[0].id ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
                <span className="text-xs md:text-sm">View Project</span>
                <i className="ri-arrow-right-line" />
              </div>
            </div>
          </div>

          {/* Small Projects Column */}
          <div className="col-span-1 md:col-span-12 lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 md:gap-6">
            {projects.slice(1, 3).map((project, index) => (
              <div
                key={project.id}
                data-project-id={project.id}
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
                className={`relative h-[30vh] md:h-[28vh] lg:h-[34vh] overflow-hidden group cursor-pointer transition-all duration-700 ${
                  visibleProjects.includes(project.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${(index + 1) * 150}ms` }}
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-80" />
                <div className={`absolute inset-0 bg-[#C9A86C]/20 transition-opacity duration-500 ${hoveredProject === project.id ? 'opacity-100' : 'opacity-0'}`} />
                
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <span className="text-[#C9A86C] text-[10px] md:text-xs tracking-widest uppercase mb-1 md:mb-2 block">{project.category}</span>
                  <h3 className="text-base md:text-lg lg:text-xl font-serif text-white">{project.title}</h3>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Row */}
          {projects.slice(3, 6).map((project, index) => (
            <div
              key={project.id}
              data-project-id={project.id}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              className={`col-span-1 md:col-span-6 lg:col-span-4 relative h-[35vh] md:h-[40vh] lg:h-[45vh] overflow-hidden group cursor-pointer transition-all duration-700 ${
                visibleProjects.includes(project.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${(index + 3) * 150}ms` }}
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-80" />
              <div className={`absolute inset-0 bg-[#C9A86C]/20 transition-opacity duration-500 ${hoveredProject === project.id ? 'opacity-100' : 'opacity-0'}`} />
              
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                <span className="text-[#C9A86C] text-[10px] md:text-xs tracking-widest uppercase mb-1 md:mb-2 block">{project.category}</span>
                <h3 className="text-base md:text-lg lg:text-xl font-serif text-white">{project.title}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-10 md:mt-16">
          <button className="group px-6 md:px-10 py-3 md:py-4 border border-[#C9A86C] text-[#C9A86C] font-medium tracking-wide transition-all duration-500 hover:bg-[#C9A86C] hover:text-[#0A0A0A] whitespace-nowrap cursor-pointer text-sm md:text-base">
            <span className="flex items-center gap-2 md:gap-3">
              View All Projects
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
